<div class="recovery-container">
  <!-- Background -->
  <div class="auth-background"></div>

  <!-- Main Card -->
  <div class="auth-card">

    <!-- Header -->
    <div class="auth-header">
      <div class="logo-section">
        <img src="/assets/befret_logo.png" alt="Befret" class="auth-logo">
        <h1 class="auth-title">{{ 'AUTH.ACCOUNT_RECOVERY.TITLE' | productionTranslate }}</h1>
      </div>
      <p class="auth-subtitle">{{ 'AUTH.ACCOUNT_RECOVERY.SUBTITLE' | productionTranslate }}</p>
    </div>

    <!-- Step 1: Email Input -->
    <div *ngIf="isEmailStep" class="step-content">
      <form [formGroup]="emailForm" (ngSubmit)="onSearchByEmail()" class="recovery-form">

        <!-- Info Box -->
        <div class="info-box">
          <svg class="info-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
          <p>{{ 'AUTH.ACCOUNT_RECOVERY.INFO_TEXT' | productionTranslate }}</p>
        </div>

        <!-- Email Field -->
        <div class="form-group">
          <label class="form-label">
            {{ 'AUTH.ACCOUNT_RECOVERY.EMAIL_LABEL' | productionTranslate }}
            <span class="required">*</span>
          </label>
          <input
            type="email"
            formControlName="email"
            class="form-input"
            [class.error]="emailControl?.invalid && emailControl?.touched"
            [placeholder]="'AUTH.ACCOUNT_RECOVERY.EMAIL_PLACEHOLDER' | productionTranslate"
            autocomplete="email">

          <!-- Validation Error -->
          <div class="field-error" *ngIf="emailControl?.invalid && emailControl?.touched">
            <span *ngIf="emailControl?.errors?.['required']">
              {{ 'AUTH.ACCOUNT_RECOVERY.ERRORS.EMAIL_REQUIRED' | productionTranslate }}
            </span>
            <span *ngIf="emailControl?.errors?.['email'] && !emailControl?.errors?.['required']">
              {{ 'AUTH.ACCOUNT_RECOVERY.ERRORS.INVALID_EMAIL' | productionTranslate }}
            </span>
          </div>
        </div>

        <!-- Error Alert -->
        <div class="auth-error" *ngIf="errorMessage">
          <svg class="error-icon" viewBox="0 0 24 24">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span>{{ errorMessage | productionTranslate }}</span>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="auth-submit-btn"
          [disabled]="emailForm.invalid || isLoading">
          <span class="btn-text" *ngIf="!isLoading">
            {{ 'AUTH.ACCOUNT_RECOVERY.SEARCH_BUTTON' | productionTranslate }}
          </span>
          <div class="btn-loader" *ngIf="isLoading">
            <div class="spinner"></div>
            <span>{{ 'AUTH.ACCOUNT_RECOVERY.SEARCHING' | productionTranslate }}</span>
          </div>
        </button>

      </form>

      <!-- Back to Login Link -->
      <div class="back-link">
        <button type="button" class="link-btn" (click)="onGoToLogin()">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          {{ 'AUTH.ACCOUNT_RECOVERY.BACK_TO_LOGIN' | productionTranslate }}
        </button>
      </div>
    </div>

    <!-- Step 2: Email Sent -->
    <div *ngIf="isEmailSentStep" class="step-content">

      <!-- Success Icon -->
      <div class="success-icon-container">
        <svg class="success-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        </svg>
      </div>

      <!-- Success Message -->
      <div class="success-message">
        <h2>{{ 'AUTH.ACCOUNT_RECOVERY.EMAIL_SENT_TITLE' | productionTranslate }}</h2>
        <p>{{ 'AUTH.ACCOUNT_RECOVERY.EMAIL_SENT_MESSAGE' | productionTranslate }}</p>
      </div>

      <!-- Profile Info -->
      <div class="profile-info" *ngIf="foundProfile">
        <div class="profile-item">
          <span class="label">{{ 'AUTH.ACCOUNT_RECOVERY.ACCOUNT_FOUND' | productionTranslate }}</span>
          <span class="value">{{ foundProfile.fullname || foundProfile.email }}</span>
        </div>
        <div class="profile-item" *ngIf="maskedPhone">
          <span class="label">{{ 'AUTH.ACCOUNT_RECOVERY.PHONE_NUMBER' | productionTranslate }}</span>
          <span class="value phone">{{ maskedPhone }}</span>
        </div>
      </div>

      <!-- Instructions -->
      <div class="instructions">
        <h3>{{ 'AUTH.ACCOUNT_RECOVERY.NEXT_STEPS' | productionTranslate }}</h3>
        <ol>
          <li>{{ 'AUTH.ACCOUNT_RECOVERY.STEP_1' | productionTranslate }}</li>
          <li>{{ 'AUTH.ACCOUNT_RECOVERY.STEP_2' | productionTranslate }}</li>
          <li>{{ 'AUTH.ACCOUNT_RECOVERY.STEP_3' | productionTranslate }}</li>
        </ol>
      </div>

      <!-- Actions -->
      <div class="action-buttons">
        <button type="button" class="btn-secondary" (click)="onResendEmail()" [disabled]="isLoading">
          <span *ngIf="!isLoading">{{ 'AUTH.ACCOUNT_RECOVERY.RESEND_EMAIL' | productionTranslate }}</span>
          <span *ngIf="isLoading">{{ 'AUTH.ACCOUNT_RECOVERY.SENDING' | productionTranslate }}</span>
        </button>
        <button type="button" class="btn-primary" (click)="onGoToLogin()">
          {{ 'AUTH.ACCOUNT_RECOVERY.GO_TO_LOGIN' | productionTranslate }}
        </button>
      </div>

      <!-- Success/Error Messages -->
      <div class="message-container">
        <div class="auth-success" *ngIf="successMessage">
          <svg class="check-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <span>{{ successMessage | productionTranslate }}</span>
        </div>
        <div class="auth-error" *ngIf="errorMessage">
          <svg class="error-icon" viewBox="0 0 24 24">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span>{{ errorMessage | productionTranslate }}</span>
        </div>
      </div>

    </div>

  </div>
</div>
